import { NgSignaturePadOptions, SignaturePadComponent } from "@almothafar/angular-signature-pad";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { PermitStep, PhotoRequirement } from "@ha/data/basic";
import { BaseModalComponent } from "@ha/ui/common";
import { Actions, ofActionCompleted, Select, Store } from "@ngxs/store";

import { untilDestroyed } from "@ngneat/until-destroy";
import { take } from "rxjs";
import { Observable } from "rxjs";
import { PermitPhoto } from "../../../permit-create/models/permit-photo.model";
import { PermitCreateActions } from "../../../permit-create/states/permit-create.actions";
import { PermitPhotoSelectors } from "../../../permit-create/states/permit-photo-selectors";
import { PermitPhotoActions } from "../../../permit-create/states/permit-photo.actions";
import { PermitCreateSignPersonModalComponentData } from "../../../shared/components/permit-create-sign-person-modal/permit-create-sign-person-modal.component";
import { PermitDetailsActions } from "../../states/permit-details.action";

@Component({
    selector: "pmt-permit-end-signature-modal",
    templateUrl: "./permit-end-signature-modal.component.html",
    styleUrls: ["./permit-end-signature-modal.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitEndSignatureModalComponent extends BaseModalComponent implements OnInit, AfterViewInit {
    @ViewChild("signature")
    public signaturePad: SignaturePadComponent;

    @ViewChild("fileInput")
    public fileInput: ElementRef<HTMLInputElement>;

    @Select(PermitPhotoSelectors.permitEndPhotos)
    public photos$: Observable<PermitPhoto[]>;

    public photoRequirement = PhotoRequirement;
    public signaturePadOptions: NgSignaturePadOptions = undefined;
    public data: PermitCreateSignPersonModalComponentData;
    public hasSignature = false;
    public disabledFireAlarmRestored = false;
    public endPermitForm: FormGroup;
    public savePermitPhotoAction = PermitPhotoActions.SavePermitPhoto;
    public endPermitWithSignatureAction = PermitDetailsActions.EndPermitWithSignature;

    constructor(
        private store: Store,
        private cdr: ChangeDetectorRef,
        private actions$: Actions,
        private formBuilder: FormBuilder,
    ) {
        super();
    }

    public ngOnInit(): void {
        this.actions$.pipe(ofActionCompleted(PermitDetailsActions.EndPermitWithSignature)).pipe(
            untilDestroyed(this),
            take(1),
        ).subscribe(() => {
            this.closeModal();
        });

        this.store.dispatch(new PermitPhotoActions.GetPermitPhotoRequirement());
        this.store.dispatch(new PermitPhotoActions.GetPermitPhotos());

        this.initForm();
    }

    private initForm(): void {
        this.endPermitForm = this.formBuilder.group({
            comment: ["", this.data.permitEndCommentRequired ? Validators.required : null],
            photos: [
                [],
                this.data.permitEndPhotoRequirement === PhotoRequirement.Required ? Validators.required : null,
            ],
        });

        // Subscribe to photos$ to update form control
        this.photos$.pipe(untilDestroyed(this)).subscribe((photos) => {
            this.endPermitForm.patchValue({ photos: photos || [] }, { emitEvent: false });
        });
    }

    public ngAfterViewInit(): void {
        this.getScreenSize();
        this.signaturePad.clear();
    }

    @HostListener("window:resize", ["$event"])
    public getScreenSize() {
        let width = 400;
        if (window.innerWidth < width) {
            width = window.innerWidth;
        }

        this.signaturePadOptions = {
            minWidth: 1,
            maxWidth: 4,
            backgroundColor: "#ffffff",
            canvasWidth: width,
            canvasHeight: width / 2,
        };

        this.cdr.detectChanges();
    }

    public clear() {
        this.signaturePad.clear();
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    public processSelectedImages(fileInput: HTMLInputElement): void {
        const images = Array.from(fileInput.files);
        for (const img of images) {
            this.store.dispatch(new PermitPhotoActions.SavePermitPhoto(this.data.permitId, img, PermitStep.ended));
        }
    }

    public removePhoto(guid: string): void {
        this.store.dispatch(new PermitPhotoActions.RemovePermitPhoto(guid)).pipe(take(1)).subscribe(() => {
            // Clear the file input after successfully removing the photo
            if (this.fileInput && this.fileInput.nativeElement) {
                this.fileInput.nativeElement.value = "";
            }
        });
    }

    public get isValid(): boolean {
        const hasRequiredFireAlarmRestored = !this.data.disabledFireAlarm || this.disabledFireAlarmRestored;
        return this.hasSignature && this.endPermitForm.valid && hasRequiredFireAlarmRestored;
    }

    public endPermit() {
        if (this.isValid) {
            this.store.dispatch(
                new PermitDetailsActions.EndPermitWithSignature({
                    permitId: this.data.permitId,
                    personId: this.data.person.personId,
                    role: this.data.roles[0],
                    roles: undefined,
                    signatureData: this.signaturePad.toDataURL(),
                    endPostWatchSignature: false,
                    personalNumber: this.data.person.personalNumber,
                    hasForeignPersonalNumber: this.data.person.hasForeignPersonalNumber,
                    comment: this.endPermitForm.get("comment").value,
                }),
            );
        }
    }

    public drawComplete(event: MouseEvent | Touch): void {
        this.hasSignature = !this.signaturePad.isEmpty();
    }

    public drawStart(event: MouseEvent | Touch): void {
    }
}
